<script setup lang="js">

import Button from "@/base-components/Button";
import BasePagination from "@/base-components/Table/BasePagination.vue";
import Vue3Datatable from "@bhplugin/vue3-datatable";
import BaseCard from "@/base-components/Card/BaseCard.vue";
import {useI18n} from "vue-i18n";
import {useNumberFormat} from "@/composables/useNumberFormat";
import {fetchDownload, useFetch} from "@/composables/useFetch";
import {base_url, uri} from "@/constants/config";
import moment from "moment-jalaali";
import {onMounted, ref} from "vue";
import Lucide from "@/base-components/Lucide";

const {t: $t} = useI18n()
moment.loadPersian({usePersianDigits: true});


const {formatNumber} = useNumberFormat();
const data = null;
const date = "";
const invoices = ref(null)
const profile = ref(null)
const isLoading = ref(true)
const pageOptions = ref({
  count: 1,
  limit: 10,
  page: 1
})
const cols = [
  {field: "counter", title: "#"},
  {field: "category", title: $t('title.category')},
  {field: "name", title: $t('title.name')},
  {field: "amount", title: $t('title.wallet-amount')},
  {field: "tax", title: $t('title.tax')},
  {field: "payedAmount", title: $t('title.gross')},
  {field: "createdAt", title: $t('title.date')},
  {field: "expireAt", title: $t('title.expire-at')},
  {field: "status", title: $t('title.status')},
  {field: "action", title: $t('title.table-action')},
]

const fetchWalletHistory = async () => {
  isLoading.value = true;
  try {
    const {data, error} = await useFetch(uri.api.wallet.invoices, {method: "GET"});
    if (!error) {
      invoices.value = data;
      pageOptions.value.count = data.meta.total
      pageOptions.value.limit = data.meta.perPage
    }
  } catch (err) {
    console.error(err);
  } finally {
    isLoading.value = false;
  }
}

function download(item) {
  fetchDownload(uri.api.wallet.invoices + '/' + item.id);
}

onMounted(async () => {
  try {
    // Run both functions in parallel
    await Promise.all([fetchWalletHistory()]);
  } catch (err) {
    console.error("Error during data fetching:", err);
  }
});


</script>

<template>
  <BaseCard
      class="col-span-12 sm:col-span-12 md:col-span-12 lg:col-span-12"
  >
    <template v-slot:body>
      <vue3-datatable
          :columns="cols"
          :rows="invoices?.data"
          :loading="isLoading"
          :totalRows="pageOptions.count"
          :isServerMode="true"
          :pageSize="pageOptions.limit"
          :pagination="false"
          :sortable="false"
          class="advanced-table text-start items-start justify-start"
          rowClass="cursor-pointer"
      >
        <!-- Empty header slot to remove the header -->
        <template #header>
          <!-- No content here will hide the header -->
        </template>
        <!-- Empty footer slot to remove the footer -->
        <template #footer>
          <!-- No content here will hide the footer -->
        </template>

        <template #counter="data,key">
          <div class="text-start">
            {{
              (invoices?.data.indexOf(data.value) + 1) + ((pageOptions.page - 1) * pageOptions.limit)
            }}
          </div>
        </template>
        <template #category="data,key">
          <div class="text-start">
            {{ $t(`invoice.${data.value.category}`)}}
          </div>
        </template>
        <template #amount="data,key">
          <div class="text-start">
            {{ formatNumber(data.value.amount) }}
          </div>
        </template>
        <template #tax="data,key">
          <div class="text-start">
            {{ formatNumber(data.value.tax) }}
          </div>
        </template>
        <template #payedAmount="data,key">
          <div class="text-start">
            {{ formatNumber(data.value.payedAmount) }}
          </div>
        </template>
        <template #createdAt="data,key">
          <div class="text-start">
            {{ moment(data.value.createdAt).locale("fa").format("jYYYY/jMM/jDD HH:mm:ss") }}
          </div>
        </template>
        <template #expireAt="data,key">
          <div class="text-start">
            {{ moment(data.value.expireAt).locale("fa-IR").format("jYYYY/jMM/jDD HH:mm:ss") }}
          </div>
        </template>
        <template #status="data,key">
          <div class="text-start">
            {{ $t(`invoice.status.${data.value.status}`) }}
          </div>
        </template>
        <template #action="data,key">
          <div class="text-start" v-if="!isLoading">
            <Button v-if="data.value.status === 1" variant="outline-success" @click="download(data.value)"
                    size="sm"
                    class="inline-block w-24 mb-2 mr-1">
              <Lucide icon="Download" class="inline-block w-16" />
              {{ $t("button.download") }}
            </Button>
          </div>
        </template>
      </vue3-datatable>
      <div
          class="flex flex-col items-center p-5 text-center sm:flex-row sm:text-start text-slate-500"
      >
        <div>

          <BasePagination
              v-if="pageOptions.count>pageOptions.limit"
              :page-options="pageOptions"
              @page-change="fetchWalletHistory"
          />

        </div>
      </div>

    </template>
  </BaseCard>
</template>

<style scoped>

</style>